import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MoreHorizontal, FileText, UploadCloud, Plus, Trash2, Database } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import { useTranslation } from 'react-i18next';

// Import API service function to manage categories
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const DocumentPage: React.FC = () => {
  const { t } = useTranslation();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedBucketId, setSelectedBucketId] = useState('');
  const [categoryModalBucketId, setCategoryModalBucketId] = useState('');

  // Enhanced upload modal state
  const [uploadModalBucketId, setUploadModalBucketId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<Record<string, { status: 'pending' | 'uploading' | 'success' | 'error', progress: number, error?: string }>>({});

  // Details Modal State
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const { user } = useAuthStore();
  const role = user?.role?.toLowerCase();

  // Robust admin check: check both 'role' and boolean flags 'is_admin'/'isAdmin'
  // as different auth providers or legacy data might use different field names
  const isAdmin =
    role === 'admin' ||
    role === 'super_admin' ||
    role === 'researcher' ||
    (user as any)?.is_admin === true ||
    (user as any)?.isAdmin === true;

  React.useEffect(() => {
    console.log('📄 DocumentPage Debug:', {
      name: user?.name,
      email: user?.email,
      rawRole: user?.role,
      processedRole: role,
      id: user?.id,
      isAdmin,
      hydrated: !!user
    });
  }, [user, role, isAdmin]);

  const queryClient = useQueryClient();

  // API functions
  const fetchCategories = async () => {
    const response = await api.get('/document-categories/');
    return response.data;
  };

  const createCategory = async ({ name, description, bucketId }: { name: string, description?: string, bucketId: string }) => {
    const response = await api.post('/document-categories/', {
      name,
      description: description || null,
      bucket_id: bucketId
    });
    return response.data;
  };

  const deleteCategory = async (categoryId: string) => {
    const response = await api.delete(`/document-categories/${categoryId}`);
    return response.data;
  };

  const deleteDocument = async (documentId: string) => {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  };

  // Query to fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['documentCategories'],
    queryFn: fetchCategories,
    enabled: !!user,
  });

  // Query to fetch documents
  const { data: documents = [], isLoading: documentsLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      const response = await api.get('/documents/');
      return response.data;
    },
    enabled: !!user,
  });

  // Query to fetch buckets
  const { data: bucketsList = [], isLoading: bucketsLoading } = useQuery({
    queryKey: ['buckets'],
    queryFn: async () => {
      const response = await api.get('/buckets');
      return response.data;
    },
    enabled: !!user,
  });

  // Query to fetch upload settings (file size, extensions)
  const { data: uploadSettings } = useQuery({
    queryKey: ['uploadSettings'],
    queryFn: async () => {
      const response = await api.get('/documents/settings');
      return response.data;
    },
    enabled: !!user && isUploadModalOpen,
  });

  // Mutation to create a category
  const createCategoryMutation = useMutation({
    mutationFn: createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentCategories'] });
      setNewCategoryName('');
      setNewCategoryDescription('');
      setIsCategoryModalOpen(false);
      alert(t('documentPage.categoryCreated', 'Category created successfully!'));
    },
    onError: (error: any) => {
      console.error('Category creation failed:', error);
      alert(t('documentPage.categoryError', 'Failed to create category: ') + (error.response?.data?.detail || error.message));
    }
  });

  // Mutation to delete a category
  const deleteCategoryMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentCategories'] });
    },
  });

  // Mutation to delete a document
  const deleteDocumentMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error: any) => {
      alert(t('documentPage.deleteError', 'Failed to delete document: ') + (error.response?.data?.detail || error.message));
    }
  });

  const handleDeleteDocument = (documentId: string) => {
    if (window.confirm(t('documentPage.confirmDeleteDocument', 'Are you sure you want to delete this document?'))) {
      deleteDocumentMutation.mutate(documentId);
    }
  };

  // Mutation to upload a document
  const uploadDocumentMutation = useMutation({
    mutationFn: async ({ file, categoryId }: { file: File, categoryId: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category_id', categoryId);
      // Backend automatically finds the bucket based on category_id, 
      // but we could explicitly pass it if the API supported it.
      // For now, the backend logic I updated earlier handles this via category lookup.

      const response = await api.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] }); // Refresh documents list
      setIsUploadModalOpen(false);
      // Reset form
      const fileInput = document.getElementById('document') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      setSelectedCategoryId('');
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="default" className="bg-green-500">Completed</Badge>;
      case 'PROCESSING':
        return <Badge variant="secondary">Processing</Badge>;
      case 'QUEUED':
        return <Badge variant="outline">Queued</Badge>;
      case 'FAILED':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const [isBucketModalOpen, setIsBucketModalOpen] = useState(false);
  const [newBucketName, setNewBucketName] = useState('');
  const [newBucketDescription, setNewBucketDescription] = useState('');

  const createBucketMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => {
      return api.post('/buckets', { ...data, visible_in_frontend: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      setIsBucketModalOpen(false);
      setNewBucketName('');
      setNewBucketDescription('');
      alert(t('documentPage.bucketCreated', 'Bucket created successfully!'));
    },
    onError: (error: any) => {
      alert(t('documentPage.bucketError', 'Failed to create bucket: ') + (error.response?.data?.detail || error.message));
    },
  });

  const handleCreateBucket = (e: React.FormEvent) => {
    e.preventDefault();
    if (newBucketName.trim()) {
      createBucketMutation.mutate({
        name: newBucketName.trim(),
        description: newBucketDescription.trim() || undefined,
      });
    }
  };

  const handleCreateCategory = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCategoryName.trim() && categoryModalBucketId) {
      createCategoryMutation.mutate({
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
        bucketId: categoryModalBucketId,
      });
    }
  };

  const handleDeleteCategory = (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      deleteCategoryMutation.mutate(categoryId);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">{t('documentPage.title', 'Document Management')}</h1>
        <div className="flex space-x-2">
          {isAdmin && (
            <>
              <Button onClick={() => setIsCategoryModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('documentPage.createCategory', 'Create Category')}
              </Button>
              <Button onClick={() => setIsBucketModalOpen(true)} className="ml-2">
                <Plus className="mr-2 h-4 w-4" />
                {t('documentPage.createBucket', 'Create Bucket')}
              </Button>
            </>
          )}
          <Button onClick={() => setIsUploadModalOpen(true)}>
            <UploadCloud className="mr-2 h-4 w-4" />
            {t('documentPage.uploadNewDocument', 'Upload New Document')}
          </Button>
        </div>
      </div>

      {
        isAdmin && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Buckets Section */}
            <div className="border rounded-lg p-4 bg-muted/30">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <Database className="mr-2 h-5 w-5" />
                {t('documentPage.buckets.title', 'Storage Buckets')}
              </h2>
              {bucketsLoading ? (
                <p>{t('documentPage.buckets.loading', 'Loading buckets...')}</p>
              ) : bucketsList.length > 0 ? (
                <div className="space-y-2">
                  {bucketsList.map((bucket: any) => (
                    <div key={bucket.id} className="flex items-center justify-between p-3 bg-white rounded border">
                      <div>
                        <span className="font-medium">{bucket.name}</span>
                        {bucket.description && (
                          <p className="text-xs text-gray-500">{bucket.description}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {bucket.is_default ? 'System Default' : 'User Created'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic text-sm">{t('documentPage.buckets.empty', 'No buckets found.')}</p>
              )}
            </div>

            {/* Categories Section */}
            <div className="border rounded-lg p-4 bg-muted/30">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                {t('documentPage.categories.title', 'Document Categories')}
              </h2>
              {categoriesLoading ? (
                <p>{t('documentPage.categories.loading', 'Loading categories...')}</p>
              ) : categories.length > 0 ? (
                <div className="space-y-2">
                  {categories.map((category: any) => (
                    <div key={category.id} className="flex items-center justify-between p-3 bg-white rounded border">
                      <div>
                        <span className="font-medium">{category.name}</span>
                        {category.description && (
                          <p className="text-xs text-gray-500">{category.description}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(category.id)}
                        disabled={deleteCategoryMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic text-sm">{t('documentPage.categories.empty', 'No categories created yet.')}</p>
              )}
            </div>
          </div>
        )
      }

      {/* TODO: Add filtering controls here */}

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('documentPage.table.filename', 'Filename')}</TableHead>
              <TableHead>{t('documentPage.table.category', 'Category')}</TableHead>
              <TableHead>{t('documentPage.table.uploadDate', 'Upload Date')}</TableHead>
              <TableHead>{t('documentPage.table.status', 'Status')}</TableHead>
              <TableHead className="text-right">{t('documentPage.table.actions', 'Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documentsLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {t('documentPage.documents.loading', 'Loading documents...')}
                </TableCell>
              </TableRow>
            ) : documents.length > 0 ? (
              documents.map((doc: any) => (
                <TableRow key={doc.id}>
                  <TableCell className="font-medium flex items-center">
                    <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                    {doc.filename}
                  </TableCell>
                  <TableCell>
                    {categories.find((cat: any) => cat.id === doc.category_id)?.name || t('documentPage.documents.unknownCategory', 'Unknown Category')}
                  </TableCell>
                  <TableCell>{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{getStatusBadge(doc.status)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">{t('documentPage.actions.openMenu', 'Open menu')}</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          setSelectedDocument(doc);
                          setIsDetailsModalOpen(true);
                        }}>{t('documentPage.actions.viewDetails', 'View Details')}</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-500" onClick={() => handleDeleteDocument(doc.id)}>{t('documentPage.actions.delete', 'Delete')}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  {t('documentPage.documents.empty', 'No documents uploaded yet.')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isUploadModalOpen} onOpenChange={(open) => {
        setIsUploadModalOpen(open);
        if (!open) {
          // Reset state when closing
          setUploadModalBucketId('');
          setSelectedCategoryId('');
          setSelectedFiles([]);
          setUploadStatuses({});
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('documentPage.uploadModal.title', 'Upload Documents')}</DialogTitle>
            <DialogDescription>
              {t('documentPage.uploadModal.description', 'Select a bucket, category, and files to upload. You can upload multiple files at once.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Step 1: Bucket Selection */}
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="upload-bucket">{t('documentPage.uploadModal.bucket', 'Storage Bucket')} *</Label>
              <Select value={uploadModalBucketId} onValueChange={(value) => {
                setUploadModalBucketId(value);
                setSelectedCategoryId(''); // Reset category when bucket changes
              }}>
                <SelectTrigger id="upload-bucket">
                  <SelectValue placeholder={t('documentPage.uploadModal.selectBucketPlaceholder', 'Select a bucket first')} />
                </SelectTrigger>
                <SelectContent>
                  {bucketsList.map((bucket: any) => (
                    <SelectItem key={bucket.id} value={bucket.id}>{bucket.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Category Selection (filtered by bucket) */}
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="upload-category">{t('documentPage.uploadModal.category', 'Category')} *</Label>
              <Select
                value={selectedCategoryId}
                onValueChange={setSelectedCategoryId}
                disabled={!uploadModalBucketId}
              >
                <SelectTrigger id="upload-category">
                  <SelectValue placeholder={
                    uploadModalBucketId
                      ? t('documentPage.uploadModal.selectCategoryPlaceholder', 'Select a category')
                      : t('documentPage.uploadModal.selectBucketFirst', 'Select a bucket first')
                  } />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const filteredCategories = categories.filter((cat: any) => cat.bucket_id === uploadModalBucketId);
                    console.log('🔍 Upload Modal Debug:', {
                      uploadModalBucketId,
                      totalCategories: categories.length,
                      filteredCategories: filteredCategories.length,
                      allCategories: categories.map((c: any) => ({ id: c.id, name: c.name, bucket_id: c.bucket_id })),
                      filtered: filteredCategories.map((c: any) => ({ id: c.id, name: c.name, bucket_id: c.bucket_id }))
                    });

                    if (filteredCategories.length === 0 && uploadModalBucketId) {
                      return (
                        <SelectItem value="no-categories" disabled>
                          No categories for this bucket - create one first
                        </SelectItem>
                      );
                    }

                    return filteredCategories.map((category: any) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            {/* Step 3: File Selection (multiple) */}
            <div className="grid w-full items-center gap-1.5">
              <div className="flex justify-between items-end mb-1">
                <Label htmlFor="upload-files">{t('documentPage.uploadModal.files', 'Files')} *</Label>
                {uploadSettings && (
                  <div className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded-sm border border-border/50">
                    <span className="font-medium">{t('documentPage.uploadModal.maxSize', 'Max Size')}:</span> {uploadSettings.max_file_size_mb}MB |
                    <span className="font-medium ml-2">{t('documentPage.uploadModal.allowed', 'Allowed')}:</span> {uploadSettings.allowed_file_types.join(', ')}
                  </div>
                )}
              </div>
              <Input
                id="upload-files"
                type="file"
                multiple
                disabled={!selectedCategoryId}
                onChange={(e) => {
                  if (e.target.files) {
                    const filesArray = Array.from(e.target.files);
                    setSelectedFiles(filesArray);
                    // Initialize upload statuses
                    const statuses: Record<string, any> = {};
                    filesArray.forEach(file => {
                      statuses[file.name] = { status: 'pending', progress: 0 };
                    });
                    setUploadStatuses(statuses);
                  }
                }}
              />
              <div className="flex flex-col gap-1.5 mt-1">
                {!selectedCategoryId && (
                  <p className="text-sm text-gray-500">{t('documentPage.uploadModal.selectCategoryFirst', 'Select bucket and category first')}</p>
                )}
                <div className="p-2 bg-blue-50/50 border border-blue-100 rounded text-[11px] text-blue-700 leading-tight">
                  <span className="font-semibold">Note:</span> {t('documentPage.uploadModal.adminNotice', 'Ask the Processing Engine administrator to increase limits or add file extensions if you need to upload a different file type or larger files.')}
                </div>
              </div>
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="border rounded-lg p-3">
                <h4 className="font-semibold mb-2">{t('documentPage.uploadModal.selectedFiles', 'Selected Files')} ({selectedFiles.length})</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between text-sm py-1 px-2 hover:bg-gray-50 rounded">
                      <span className="truncate flex-1">{file.name}</span>
                      <span className="text-gray-500 ml-2">{(file.size / 1024).toFixed(1)} KB</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 h-6 w-6 p-0"
                        onClick={() => {
                          const newFiles = selectedFiles.filter((_, i) => i !== index);
                          setSelectedFiles(newFiles);
                          const newStatuses = { ...uploadStatuses };
                          delete newStatuses[file.name];
                          setUploadStatuses(newStatuses);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Status Table */}
            {Object.keys(uploadStatuses).length > 0 && Object.values(uploadStatuses).some((s: any) => s.status !== 'pending') && (
              <div className="border rounded-lg p-3">
                <h4 className="font-semibold mb-2">{t('documentPage.uploadModal.uploadStatus', 'Upload Status')}</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('documentPage.uploadModal.filename', 'Filename')}</TableHead>
                      <TableHead>{t('documentPage.uploadModal.status', 'Status')}</TableHead>
                      <TableHead>{t('documentPage.uploadModal.progress', 'Progress')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(uploadStatuses).map(([filename, status]: [string, any]) => (
                      <TableRow key={filename}>
                        <TableCell className="font-medium truncate max-w-xs">{filename}</TableCell>
                        <TableCell>
                          {status.status === 'pending' && <Badge variant="outline">Pending</Badge>}
                          {status.status === 'uploading' && <Badge variant="secondary">Uploading...</Badge>}
                          {status.status === 'success' && <Badge className="bg-green-500">Success</Badge>}
                          {status.status === 'error' && <Badge variant="destructive">Error</Badge>}
                        </TableCell>
                        <TableCell>
                          {status.status === 'uploading' && `${status.progress}%`}
                          {status.status === 'success' && '100%'}
                          {status.status === 'error' && status.error}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadModalOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              onClick={async () => {
                if (!uploadModalBucketId || !selectedCategoryId || selectedFiles.length === 0) return;

                // Upload files sequentially
                for (const file of selectedFiles) {
                  try {
                    // Update status to uploading
                    setUploadStatuses(prev => ({
                      ...prev,
                      [file.name]: { status: 'uploading', progress: 0 }
                    }));

                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('category_id', selectedCategoryId);

                    await api.post('/documents/upload', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                      onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                          setUploadStatuses(prev => ({
                            ...prev,
                            [file.name]: { status: 'uploading', progress }
                          }));
                        }
                      }
                    });

                    // Mark as success
                    setUploadStatuses(prev => ({
                      ...prev,
                      [file.name]: { status: 'success', progress: 100 }
                    }));
                  } catch (error: any) {
                    // Extract detailed error message from backend if available
                    // Usually: error.response.data.detail
                    const errorMessage = error.response?.data?.detail || error.message || 'Upload failed';

                    // Mark as error
                    setUploadStatuses(prev => ({
                      ...prev,
                      [file.name]: { status: 'error', progress: 0, error: errorMessage }
                    }));
                  }
                }

                // Refresh documents list
                queryClient.invalidateQueries({ queryKey: ['documents'] });
                queryClient.invalidateQueries({ queryKey: ['documents'] });

                // Auto-close after 2 seconds if all successful
                const allSuccess = Object.values(uploadStatuses).every((s: any) => s.status === 'success');
                if (allSuccess) {
                  setTimeout(() => {
                    setIsUploadModalOpen(false);
                  }, 2000);
                }
              }}
              disabled={!uploadModalBucketId || !selectedCategoryId || selectedFiles.length === 0 || Object.values(uploadStatuses).some((s: any) => s.status === 'uploading')}
            >
              {Object.values(uploadStatuses).some((s: any) => s.status === 'uploading')
                ? t('documentPage.uploadModal.uploading', 'Uploading...')
                : t('documentPage.uploadModal.upload', `Upload ${selectedFiles.length} File(s)`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('documentPage.categoryModal.title', 'Create New Document Category')}</DialogTitle>
            <DialogDescription>
              {t('documentPage.categoryModal.description', 'Add a new category that users can assign to their documents.')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCategory}>
            <div className="grid gap-4 py-4">
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="category-name">{t('documentPage.categoryModal.nameLabel', 'Category Name')}</Label>
                <Input
                  id="category-name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder={t('documentPage.categoryModal.namePlaceholder', 'Enter category name')}
                  required
                />
              </div>
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="category-description">{t('documentPage.categoryModal.descriptionLabel', 'Description (Optional)')}</Label>
                <Input
                  id="category-description"
                  value={newCategoryDescription}
                  onChange={(e) => setNewCategoryDescription(e.target.value)}
                  placeholder={t('documentPage.categoryModal.descriptionPlaceholder', 'Enter category description')}
                />
              </div>
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="category-bucket">{t('documentPage.categoryModal.bucketLabel', 'Storage Bucket')}</Label>
                <Select value={categoryModalBucketId} onValueChange={setCategoryModalBucketId}>
                  <SelectTrigger id="category-bucket">
                    <SelectValue placeholder={t('documentPage.categoryModal.selectBucketPlaceholder', 'Select a bucket')} />
                  </SelectTrigger>
                  <SelectContent>
                    {bucketsList.map((bucket: any) => (
                      <SelectItem key={bucket.id} value={bucket.id}>{bucket.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setIsCategoryModalOpen(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={createCategoryMutation.isPending}>
                {createCategoryMutation.isPending ? t('documentPage.categoryModal.creating', 'Creating...') : t('documentPage.categoryModal.create', 'Create Category')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isBucketModalOpen} onOpenChange={setIsBucketModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('documentPage.bucketModal.title', 'Create New Bucket')}</DialogTitle>
            <DialogDescription>
              {t('documentPage.bucketModal.description', 'Create a new storage bucket (datasets folder) for organizing data.')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateBucket}>
            <div className="grid gap-4 py-4">
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="bucket-name">{t('documentPage.bucketModal.nameLabel', 'Bucket Name')}</Label>
                <Input
                  id="bucket-name"
                  value={newBucketName}
                  onChange={(e) => setNewBucketName(e.target.value)}
                  placeholder={t('documentPage.bucketModal.namePlaceholder', 'Enter bucket name (e.g., project-alpha)')}
                  required
                />
              </div>
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="bucket-description">{t('documentPage.bucketModal.descriptionLabel', 'Description (Optional)')}</Label>
                <Input
                  id="bucket-description"
                  value={newBucketDescription}
                  onChange={(e) => setNewBucketDescription(e.target.value)}
                  placeholder={t('documentPage.bucketModal.descriptionPlaceholder', 'Enter bucket description')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setIsBucketModalOpen(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={createBucketMutation.isPending}>
                {createBucketMutation.isPending ? t('documentPage.bucketModal.creating', 'Creating...') : t('documentPage.bucketModal.create', 'Create Bucket')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Document Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('documentPage.details.title', 'Document Details')}</DialogTitle>
          </DialogHeader>
          {selectedDocument && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t('documentPage.details.filename', 'Filename')}</Label>
                  <p className="font-medium">{selectedDocument.filename}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('documentPage.details.status', 'Status')}</Label>
                  <div className="mt-1">{getStatusBadge(selectedDocument.status)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('documentPage.details.category', 'Category')}</Label>
                  <p className="font-medium">
                    {categories.find((cat: any) => cat.id === selectedDocument.category_id)?.name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Uploaded At</Label>
                  <p>{new Date(selectedDocument.created_at).toLocaleString()}</p>
                </div>
              </div>

              {selectedDocument.storage_path && (
                <div className="p-3 bg-muted rounded-md overflow-hidden">
                  <Label className="text-xs text-muted-foreground mb-1 block">Storage Path</Label>
                  <code className="text-xs break-all">{selectedDocument.storage_path}</code>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button onClick={() => setIsDetailsModalOpen(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>

  );
};

export default DocumentPage;
